/**
 * Increments a letter sequence as bijective base-26 ("spreadsheet columns"),
 * preserving the case of the input: A -> B, Z -> AA, az -> ba.
 */
const incrementLetters = (letters: string, by: number): string => {
    const isLowerCase = letters === letters.toLowerCase();
    let n = 0;
    for (const char of letters.toUpperCase()) {
        n = n * 26 + (char.charCodeAt(0) - 64);
    }
    n += by;
    let result = '';
    while (n > 0) {
        let remainder = n % 26;
        if (remainder === 0) remainder = 26;
        result = String.fromCharCode((isLowerCase ? 96 : 64) + remainder) + result;
        n = (n - remainder) / 26;
    }
    return result;
};

const incrementDigits = (digits: string, by: number): string => {
    // BigInt keeps long numeric serials (> 15 digits) precise
    return String(BigInt(digits) + BigInt(by)).padStart(digits.length, '0');
};

/**
 * Suggests titles for duplicates of a template node by continuing the
 * counting scheme found in the template title:
 *  - trailing digits:        "WCARH01" -> WCARH02, WCARH03, ... (padding preserved)
 *  - leading digits:         "1L"      -> 2L, 3L, ...
 *  - trailing single letter: "NRD-A"   -> NRD-B, ... NRD-Z, NRD-AA, ...
 *  - otherwise:              "Pump"    -> Pump-001, Pump-002, ...
 */
export const generateDuplicateTitles = (templateTitle: string, count: number): string[] => {
    const title = templateTitle ?? '';
    const indexes = Array.from({length: Math.max(count, 0)}, (_, i) => i + 1);

    const trailingDigits = title.match(/^(.*?)(\d+)$/);
    if (trailingDigits) {
        const [, prefix, digits] = trailingDigits;
        return indexes.map((i) => `${prefix}${incrementDigits(digits, i)}`);
    }

    const leadingDigits = title.match(/^(\d+)(.*)$/);
    if (leadingDigits) {
        const [, digits, rest] = leadingDigits;
        return indexes.map((i) => `${incrementDigits(digits, i)}${rest}`);
    }

    // A single trailing letter only counts as a sequence marker when it doesn't
    // belong to a word (preceded by a non-letter, or the whole title is one letter).
    const trailingLetter = title.match(/^(.*[^A-Za-z])([A-Za-z])$/) ?? title.match(/^()([A-Za-z])$/);
    if (trailingLetter) {
        const [, prefix, letter] = trailingLetter;
        return indexes.map((i) => `${prefix}${incrementLetters(letter, i)}`);
    }

    return indexes.map((i) => `${title}-${String(i).padStart(3, '0')}`);
};
